import { apiClient } from "@/lib/apiClient";

// Wholesaler types and interfaces
export interface Wholesaler {
  id: string;
  name: string;
  description?: string;
  logo?: string;
  website?: string;
  contact_email?: string;
  contact_phone?: string;
  status: "active" | "inactive";
  created_at: string;
  updated_at: string;
  products_count?: number;
  collections_count?: number;
}

export interface WholesalerProduct {
  id: string;
  title: string;
  description?: string;
  price: number;
  compare_at_price?: number;
  sku?: string;
  images?: string[];
  variants?: any[];
  wholesaler_id: string;
  wholesaler_name?: string;
  is_mapped?: boolean;
  mapped_product_id?: string;
}

export interface WholesalerCollection {
  id: string;
  title: string;
  description?: string;
  image?: string;
  products_count?: number;
  wholesaler_id: string;
  wholesaler_name?: string;
  is_mapped?: boolean;
  mapped_collection_id?: string;
}

export interface FetchWholesalersOptions {
  page?: number;
  pageSize?: number;
  status?: "active" | "inactive";
}

export interface SearchWholesalersOptions {
  query: string;
  page?: number;
  pageSize?: number;
}

export interface FetchWholesalerProductsOptions {
  page?: number;
  pageSize?: number;
  status?: string;
  price_min?: number;
  price_max?: number;
}

export interface SearchWholesalerProductsOptions {
  query: string;
  page?: number;
  pageSize?: number;
  price_min?: number;
  price_max?: number;
}

export interface FetchWholesalerCollectionsOptions {
  page?: number;
  pageSize?: number;
}

export interface SearchWholesalerCollectionsOptions {
  query: string;
  page?: number;
  pageSize?: number;
}

export interface ImportProductsRequest {
  product_ids: string[];
  margin_type?: "fixed" | "percentage";
  margin_value?: number;
  apply_to_variants?: boolean;
}

export interface ImportCollectionsRequest {
  collection_ids: string[];
  margin_type?: "fixed" | "percentage";
  margin_value?: number;
  import_products?: boolean;
}

export interface UnmapProductsRequest {
  product_ids: string[];
}

export interface UnmapCollectionsRequest {
  collection_ids: string[];
}

// Wholesaler Management
export const getWholesalers = async (options: FetchWholesalersOptions = {}) => {
  const params: Record<string, string> = {};

  if (options.page) params.page = options.page.toString();
  if (options.pageSize) params.pageSize = options.pageSize.toString();
  if (options.status) params.status = options.status;

  return await apiClient.getPaginated("/api/v1/admin/:storeId/wholesaler", params);
};

export const searchWholesalers = async (options: SearchWholesalersOptions) => {
  const params: Record<string, string> = {
    q: options.query,
  };

  if (options.page) params.page = options.page.toString();
  if (options.pageSize) params.pageSize = options.pageSize.toString();

  return await apiClient.getPaginated("/api/v1/admin/:storeId/wholesaler/search", params);
};

export const getWholesalerById = async (wholesalerId: string) => {
  return await apiClient.get(`/api/v1/admin/:storeId/wholesaler/${wholesalerId}`);
};

// Wholesaler Product Operations
export const getWholesalerProducts = async (wholesalerId: string, options: FetchWholesalerProductsOptions = {}) => {
  const params: Record<string, string> = {};

  if (options.page) params.page = options.page.toString();
  if (options.pageSize) params.pageSize = options.pageSize.toString();
  if (options.status) params.status = options.status;
  if (options.price_min) params.price_min = options.price_min.toString();
  if (options.price_max) params.price_max = options.price_max.toString();

  return await apiClient.getPaginated(`/api/v1/admin/:storeId/wholesaler/${wholesalerId}/products`, params);
};

export const searchWholesalerProducts = async (wholesalerId: string, options: SearchWholesalerProductsOptions) => {
  const params: Record<string, string> = {
    q: options.query,
  };

  if (options.page) params.page = options.page.toString();
  if (options.pageSize) params.pageSize = options.pageSize.toString();
  if (options.price_min) params.price_min = options.price_min.toString();
  if (options.price_max) params.price_max = options.price_max.toString();

  return await apiClient.getPaginated(`/api/v1/admin/:storeId/wholesaler/${wholesalerId}/search-products`, params);
};

export const importProductsFromWholesaler = async (wholesalerId: string, request: ImportProductsRequest) => {
  return await apiClient.put(`/api/v1/admin/:storeId/wholesaler/${wholesalerId}/import-products`, request);
};

export const unmapProductsFromWholesaler = async (wholesalerId: string, request: UnmapProductsRequest) => {
  return await apiClient.put(`/api/v1/admin/:storeId/wholesaler/${wholesalerId}/unmap-products`, request);
};

// Wholesaler Collection Operations
export const getWholesalerCollections = async (wholesalerId: string, options: FetchWholesalerCollectionsOptions = {}) => {
  const params: Record<string, string> = {};

  if (options.page) params.page = options.page.toString();
  if (options.pageSize) params.pageSize = options.pageSize.toString();

  return await apiClient.getPaginated(`/api/v1/admin/:storeId/wholesaler/${wholesalerId}/collections`, params);
};

export const searchWholesalerCollections = async (wholesalerId: string, options: SearchWholesalerCollectionsOptions) => {
  const params: Record<string, string> = {
    q: options.query,
  };

  if (options.page) params.page = options.page.toString();
  if (options.pageSize) params.pageSize = options.pageSize.toString();

  return await apiClient.getPaginated(`/api/v1/admin/:storeId/wholesaler/${wholesalerId}/search-collections`, params);
};

export const importCollectionsFromWholesaler = async (wholesalerId: string, request: ImportCollectionsRequest) => {
  return await apiClient.put(`/api/v1/admin/:storeId/wholesaler/${wholesalerId}/import-collections`, request);
};

export const unmapCollectionsFromWholesaler = async (wholesalerId: string, request: UnmapCollectionsRequest) => {
  return await apiClient.put(`/api/v1/admin/:storeId/wholesaler/${wholesalerId}/unmap-collections`, request);
};